import React, { useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { ReportSection, DeltaChip, Stat, Panel } from "./ReportSection";
import { cn, dateLocale } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

const SERIES = [
  { key: "created", label: tfr("Créés"), color: "#3B82F6" },
  { key: "sent", label: tfr("Envoyés"), color: "#F5A524" },
  { key: "signed", label: tfr("Signés"), color: "#22C55E" },
  { key: "completed", label: tfr("Finalisés"), color: "#10B981" },
  { key: "archived", label: tfr("Archivés"), color: "#71717A" },
  { key: "cancelled", label: tfr("Annulés"), color: "#E5484D" },
];

const AXIS_TICK = { fontSize: 11, fill: "rgba(160,165,175,0.7)" };

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const date = new Date(`${label}T00:00:00`).toLocaleDateString(dateLocale(), { day: "numeric", month: "short", year: "numeric" });
  return (
    <div className="chart-tooltip-surface rounded-lg px-3 py-2 text-xs" style={{ pointerEvents: "none" }}>
      <p className="font-medium mb-1">{date}</p>
      {payload.filter((p) => p.value > 0).map((p) => (
        <p key={p.dataKey} className="flex items-center gap-1.5 text-[color:var(--chart-tooltip-muted)]">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: p.color }} />
          {SERIES.find((s) => s.key === p.dataKey)?.label} : {p.value}
        </p>
      ))}
      {payload.every((p) => !p.value) && <p className="text-[color:var(--chart-tooltip-muted)] opacity-70">{tfr("Aucune activité")}</p>}
    </div>
  );
};

export const SectionActivity = ({ activity }) => {
  const [hidden, setHidden] = useState(() => new Set(["archived", "cancelled"]));
  const toggle = (key) => setHidden((h) => {
    const next = new Set(h);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const stats = activity?.stats || {};
  const tick = useMemo(() => (v) => {
    const d = new Date(`${v}T00:00:00`);
    return d.toLocaleDateString(dateLocale(), { day: "numeric", month: "short" });
  }, []);

  return (
    <ReportSection number="01" title={tfr("Activité contractuelle")}
      purpose={tfr("Évolution de votre production contractuelle sur la période.")} testid="section-activity">
      <Panel>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div className="flex items-center gap-2 flex-wrap" data-testid="activity-legend">
            {SERIES.map((s) => {
              const off = hidden.has(s.key);
              return (
                <button key={s.key} onClick={() => toggle(s.key)} data-testid={`activity-toggle-${s.key}`}
                  className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-opacity",
                    off ? "opacity-35 border-white/[0.08]" : "border-white/[0.12]")}>
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.color }} />
                  {s.label}
                  <span className="text-muted-foreground tabular-nums">{activity?.totals?.[s.key] ?? 0}</span>
                </button>
              );
            })}
          </div>
          <DeltaChip delta={activity?.delta} testid="activity-delta" />
        </div>
        <div style={{ height: 260, minHeight: 260 }} data-testid="activity-chart">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={activity?.timeline || []} margin={{ top: 4, right: 8, bottom: 0, left: -18 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
              <XAxis dataKey="d" tick={AXIS_TICK} tickFormatter={tick} tickLine={false} axisLine={false} minTickGap={40} />
              <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip content={<ChartTooltip />} isAnimationActive={false} />
              {SERIES.filter((s) => !hidden.has(s.key)).map((s) => (
                <Line key={s.key} type="monotone" dataKey={s.key} stroke={s.color} strokeWidth={1.8}
                  dot={false} activeDot={{ r: 3.5 }} isAnimationActive={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-6 pt-5 border-t border-white/[0.05]">
          <Stat label={tfr("Pic d'activité")} testid="activity-peak"
            value={stats.peak ? `${stats.peak.count} ${tfr("événements")}` : "—"} sub={stats.peak?.date} />
          <Stat label={tfr("Contrats créés / semaine")} value={stats.avgPerWeek ?? "—"} testid="activity-avg-week" />
          <Stat label={tfr("Mois le plus productif")} testid="activity-top-month"
            value={stats.topMonth?.label ?? "—"} sub={stats.topMonth ? `${stats.topMonth.count} ${tfr("événements")}` : null} />
          <Stat label={tfr("Plus longue inactivité")} testid="activity-inactivity"
            value={stats.longestInactivityDays != null ? `${stats.longestInactivityDays} j` : "—"} />
        </div>
      </Panel>
    </ReportSection>
  );
};
